import { DateTime } from 'luxon';
import moment from 'moment';
import { RRule } from 'rrule';
import { getTimezone } from '@shared/util/timezone';

export function toString(rule: string): string {
  const string = RRule.fromString(rule)
    // @ts-expect-error -- strict migration
    .toText(text => {
      if (text === 'for') {
        return ',';
      }
      if (text === '(~ approximate)') {
        return undefined;
      }
      return text.toString();
    })
    .trim();
  return string.charAt(0).toUpperCase() + string.slice(1);
}

// RRule handles JS Dates in a very weird way. To understand more:
// https://github.com/jakubroztocil/rrule#important-use-utc-dates
export function toRRuleDate(date: Date): Date {
  return DateTime.fromJSDate(date).setZone('UTC', { keepLocalTime: true }).toJSDate();
}

export function fromRRuleDate(date: Date): Date {
  return DateTime.fromJSDate(date).toUTC().setZone('local', { keepLocalTime: true }).toJSDate();
}

export function getDefaultDateFromRecurrence(recurrence: string, dateString?: string): Date {
  const rule = RRule.fromString(recurrence);
  const startDate = toRRuleDate(
    // Note(fant): moment(null) returns an invalid date
    moment(!!dateString ? dateString : undefined)
      .startOf('day')
      .toDate(),
  );
  const nextRecurrence = rule.after(startDate, true);
  if (!nextRecurrence?.getTime()) {
    // @ts-expect-error -- strict migration
    return null;
  }
  const nextRecurrenceInLocalTime = fromRRuleDate(nextRecurrence);
  if (!!dateString) {
    const prevDate = new Date(dateString);
    nextRecurrenceInLocalTime.setHours(prevDate.getHours());
    nextRecurrenceInLocalTime.setMinutes(prevDate.getMinutes());
    nextRecurrenceInLocalTime.setSeconds(prevDate.getSeconds());
    nextRecurrenceInLocalTime.setMilliseconds(prevDate.getMilliseconds());
  } else {
    nextRecurrenceInLocalTime.setHours(9);
    nextRecurrenceInLocalTime.setMinutes(0);
    nextRecurrenceInLocalTime.setSeconds(0);
    nextRecurrenceInLocalTime.setMilliseconds(0);
  }
  return nextRecurrenceInLocalTime;
}

export function matchRecurrenceStartDateWithDate(rule: RRule, date: Date): RRule {
  const unit = ((): moment.unitOfTime.StartOf => {
    if (rule.options.freq === RRule.MINUTELY) {
      return 'minute';
    }
    if (rule.options.freq === RRule.HOURLY) {
      return 'hour';
    }
    if (rule.options.freq === RRule.DAILY) {
      return 'day';
    }
    if (rule.options.freq === RRule.WEEKLY) {
      return 'day';
    }
    if (rule.options.freq === RRule.MONTHLY) {
      return 'day';
    }
    if (rule.options.freq === RRule.YEARLY) {
      return 'day';
    }
    // @ts-expect-error -- strict migration
    return undefined;
  })();
  if (!unit) {
    return rule;
  }

  const startDate = toRRuleDate(moment(date).startOf(unit).toDate());
  const ruleWithStartDate = new RRule({ ...rule.origOptions, dtstart: startDate });
  const nextRecurrence = ruleWithStartDate.after(startDate, true);

  const updatedRule = new RRule({
    ...rule.origOptions,
    dtstart: nextRecurrence,
    tzid: getTimezone(),
  });
  return updatedRule;
}

export function getRecurrenceBefore(recurrence: string, date: Date): Date {
  const rruleDate = toRRuleDate(date);
  const rule = RRule.fromString(recurrence);
  const rruleDateBefore = rule.before(rruleDate, true);
  if (!rruleDateBefore) {
    // @ts-expect-error -- strict migration
    return undefined;
  }
  return fromRRuleDate(rruleDateBefore);
}

export function getRecurrencesBetween(recurrence: string, startDate: Date, endDate: Date): Date[] {
  const rruleStartDate = toRRuleDate(startDate);
  const rruleEndDate = toRRuleDate(endDate);
  const rule = RRule.fromString(recurrence);
  const rruleDates = rule.between(rruleStartDate, rruleEndDate);
  return rruleDates.map(fromRRuleDate);
}
